import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Reads the current --foreground CSS custom property (HSL form "H S% L%")
// and converts it to a THREE.Color.
// Falls back to a neutral gray if parsing fails.
// ---------------------------------------------------------------------------
function getForegroundColor(): THREE.Color {
  try {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--foreground')
      .trim();
    // Tailwind CSS vars are bare HSL components: "0 0% 9%"
    const [h, s, l] = raw.split(' ').map((v) => parseFloat(v));
    if (!isNaN(h) && !isNaN(s) && !isNaN(l)) {
      return new THREE.Color(`hsl(${h}, ${s}%, ${l}%)`);
    }
  } catch {
    // ignore
  }
  return new THREE.Color(0x888888);
}

// ---------------------------------------------------------------------------
// WireframeBackground
// A fixed full-viewport canvas rendered with plain Three.js (no R3F).
// It shows a slowly rotating icosphere + a grid plane, both drawn as
// wireframes in the foreground color at very low opacity.
// ---------------------------------------------------------------------------
export function WireframeBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ── Renderer ────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);

    // ── Scene & Camera ───────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 200);
    camera.position.set(0, 6, 18);
    camera.lookAt(-5, 0, -3);

    // ── Material factory ─────────────────────────────────────────────────────
    // We recreate materials when the theme changes (handled by a MutationObserver).
    let fgColor = getForegroundColor();

    function makeMaterial(opacity: number) {
      return new THREE.LineBasicMaterial({
        color: fgColor,
        transparent: true,
        opacity,
      });
    }

    // ── Large central icosphere ──────────────────────────────────────────────
    const sphereGeo = new THREE.IcosahedronGeometry(8, 24);
    const sphereWireGeo = new THREE.WireframeGeometry(sphereGeo);
    const sphereMat = makeMaterial(0.04);
    const sphere = new THREE.LineSegments(sphereWireGeo, sphereMat);
    sphere.rotation.x = 0.3;
    scene.add(sphere);

    // ── Resize handling ──────────────────────────────────────────────────────
    function resize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener('resize', resize);

    // ── Theme change observation ─────────────────────────────────────────────
    // Watch for class changes on <html> (the ThemeContext adds/removes .dark/.light)
    const themeObserver = new MutationObserver(() => {
      fgColor = getForegroundColor();

      sphereMat.color.copy(fgColor);
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // ── Animation loop ───────────────────────────────────────────────────────
    let rafId: number;
    const clock = new THREE.Clock();

    function animate() {
      rafId = requestAnimationFrame(animate);
      const elapsed = clock.getElapsedTime();

      // Sphere: one full rotation every ~90 seconds on Y, slow wobble on X/Z
      sphere.rotation.y = (elapsed * (Math.PI * 2)) / 720;
      sphere.rotation.x = 0.3 + Math.sin(elapsed * 0.04) * 0.05;
      sphere.rotation.z = Math.cos(elapsed * 0.03) * 0.03;

      renderer.render(scene, camera);
    }
    animate();

    // ── Cleanup ──────────────────────────────────────────────────────────────
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      themeObserver.disconnect();

      sphereGeo.dispose();
      sphereWireGeo.dispose();
      sphereMat.dispose();

      renderer.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
    />
  );
}
